import { LanguageSelect } from '@/components/LanguageSelect';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { Teleprompter } from '@/components/Teleprompter';
import { toast } from '@/components/toast';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import CameraMinimalistic from '@/images/CameraMinimalistic.svg';
import Rename from '@/images/Rename.svg';
import { createConsent } from '@/pages/avatar/api';
import { useCreateAvatarStore } from '@/pages/avatar/common/context';
import { AvatarCreateTab } from '@/pages/avatar/common/types';
import { useAnalytics } from '@/services/analytics';
import { logger } from '@/services/logger';
import { MixPanelEvent } from '@/utils/types';
import styled from '@emotion/styled';
import { CircularProgress, TextField } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { StepTitle } from '../Styled/Title';
import { ConsentSelection } from './ConsentSelection';
import { ConsentVideo } from './ConsentVideo';
import { validateInputLanguage } from './validate-username';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    position: relative;
    padding-bottom: 154px;
`;

const Subcontainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 540px;
`;

const SubTitle = styled.span`
    color: ${({ theme }) => theme.colors.secondary[700]};
    text-align: center;
    font-size: 16px;
    font-weight: 400;
    letter-spacing: 0.15px;
`;

const StyledLanguageSelect = styled(LanguageSelect)`
    margin-right: auto;

    input,
    .MuiInputBase-root {
        cursor: pointer !important;
    }
`;

const StyledTooltip = styled(CustomTooltip)`
    width: 100%;
`;

const RecordTile = styled.div<{ disabled: boolean }>`
    align-items: center;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: 300px;
    transition: background linear 0.3s;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    margin: 16px 0;

    &:hover {
        background: ${({ theme }) => theme.colors.black[100]};
    }
`;

const Image = styled.img`
    width: 116px;
    height: 116px;
    margin-bottom: 16px;
`;

const UploadBoxMainText = styled.div`
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.48px;
`;

const SentenceContainer = styled.div`
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.black[50]};
    width: 100%;
    padding: 16px 24px;
    display: flex;
    flex-direction: row;
    justify-content: center;
`;

const Sentence = styled.span`
    color: ${({ theme }) => theme.colors.secondary.main};
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 26.4px;
    letter-spacing: 0.15px;
`;

const TextFieldWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 0 4px;
`;

const StyledTextField = styled(TextField)<{ width: number }>`
    height: 34px;
    width: ${({ width }) => width + 52}px;
    min-width: 140px;
    max-width: 470px;
    background-color: white;

    .MuiOutlinedInput-root fieldset.MuiOutlinedInput-notchedOutline {
        border-color: ${({ theme }) => theme.colors.black[300]} !important;
        border-width: 1px;
        border-radius: 8px;
    }

    .MuiOutlinedInput-root.Mui-focused fieldset.MuiOutlinedInput-notchedOutline {
        border-color: ${({ theme }) => theme.colors.secondary.main} !important;
    }

    .MuiInputBase-root {
        height: 34px;
    }
`;

const MeasureSpan = styled.span`
    visibility: hidden;
    position: absolute;
    white-space: nowrap;
`;

const StyledIcon = styled(MaskedIcon)`
    cursor: pointer;
`;

const USER_NAME_PLACEHOLDER = '[user name]';

export const AvatarConsent = observer(() => {
    const mixpanel = useAnalytics();

    const createAvatarStore = useCreateAvatarStore();

    const [teleprompterOpen, setTeleprompterOpen] = useState(false);
    const [errorToastId, setErrorToastId] = useState<string | number | null>(null);
    const spanRef = useRef<HTMLSpanElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [inputWidth, setInputWidth] = useState((spanRef.current?.offsetWidth || 90) + 2);

    const language = createAvatarStore.consentLanguage;
    const script = createAvatarStore.consentScript;

    useEffect(() => {
        if (spanRef.current) {
            // Update input width based on the span width plus a little extra padding
            setInputWidth(spanRef.current.offsetWidth + 2);
        }
    }, [createAvatarStore.username]);

    const reloadConsent = async () => {
        try {
            const response = await createConsent(language);
            createAvatarStore.setConsentScript(response.text);
            createAvatarStore.setUploadedConsentId(response.id);
        } catch (error) {
            logger.error('Failed to reload consent', error);
        }
    };

    useEffect(() => {
        // if teleprompter is open or consent is already selected, don't reload consent
        if (teleprompterOpen || createAvatarStore.selectedConsent || createAvatarStore.consentFile) {
            return;
        }

        createAvatarStore.setConsentScript('');
        reloadConsent();
        const interval = setInterval(reloadConsent, 1000 * 60 * 5);

        return () => {
            clearInterval(interval);
        };
    }, [language, teleprompterOpen, createAvatarStore.selectedConsent, createAvatarStore.consentFile]);

    const handleVideo = async (blob: Blob | null) => {
        if (!blob) {
            return createAvatarStore.setConsentFile(null);
        }
        mixpanel?.track('avatar-consent', {
            type: 'record',
            language,
            event: MixPanelEvent.CLICK,
        });

        const file = new File([blob], 'video.mp4', { type: 'video/mp4' });
        createAvatarStore.setConsentFile(file);

        createAvatarStore.handleConsent();
    };

    const handleFocus = () => {
        inputRef.current?.focus();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isValid = validateInputLanguage(e.target.value, language);
        if (!isValid) {
            const id = toast.error({
                message: 'Please use only characters from the selected language',
                prevId: errorToastId,
            });
            setErrorToastId(id);
            return;
        }
        createAvatarStore.setUsername(e.target.value);
    };

    // replace the 3 dynamic words with ***
    const scriptPreview = script.split(' ').slice(0, -3).join(' ') + ' ***, ***, ***';
    const previewParts = scriptPreview.split(USER_NAME_PLACEHOLDER);
    const scriptWithUsername = script.replaceAll(USER_NAME_PLACEHOLDER, createAvatarStore.username);

    const handleStartRecord = () => {
        if (!script || !createAvatarStore.usernameIsValid) {
            return;
        }

        setTeleprompterOpen(true);
    };

    return (
        <>
            <Container>
                <StepTitle>Record consent</StepTitle>
                <SubTitle>
                    To prevent technology misuse, we need to confirm the person in the video is you by reading a consent statement and
                    dynamic passcode.
                </SubTitle>

                <Subcontainer>
                    <StyledLanguageSelect
                        value={createAvatarStore.consentLanguage}
                        onChange={value => {
                            createAvatarStore.setConsentLanguage(value);
                            if (!validateInputLanguage(createAvatarStore.username)) {
                                createAvatarStore.setUsername('');
                            }
                        }}
                    />

                    {!createAvatarStore.isConsentValid && (
                        <SentenceContainer>
                            {script ? (
                                <Sentence>
                                    <span>{previewParts[0]}</span>
                                    <TextFieldWrapper>
                                        <StyledTextField
                                            type="text"
                                            size="small"
                                            value={createAvatarStore.username}
                                            onChange={handleInputChange}
                                            placeholder={USER_NAME_PLACEHOLDER}
                                            inputRef={inputRef}
                                            InputProps={{
                                                endAdornment: <StyledIcon src={Rename} size="16px" onClick={handleFocus} />,
                                            }}
                                            width={inputWidth}
                                        />
                                        <MeasureSpan ref={spanRef}>{createAvatarStore.username || ' '}</MeasureSpan>
                                    </TextFieldWrapper>
                                    {previewParts[1]}
                                </Sentence>
                            ) : (
                                <CircularProgress />
                            )}
                        </SentenceContainer>
                    )}

                    {!createAvatarStore.isConsentValid && createAvatarStore.tab === AvatarCreateTab.CONSENT && (
                        <StyledTooltip title="Please set correct username" disableHoverListener={createAvatarStore.usernameIsValid}>
                            <RecordTile onClick={handleStartRecord} disabled={!script || !createAvatarStore.usernameIsValid}>
                                <Image src={CameraMinimalistic} />
                                <UploadBoxMainText>Record with webcam</UploadBoxMainText>
                            </RecordTile>
                        </StyledTooltip>
                    )}

                    {createAvatarStore.isConsentValid && createAvatarStore.consentVideoProps && (
                        <ConsentVideo {...createAvatarStore.consentVideoProps} />
                    )}
                </Subcontainer>

                {!createAvatarStore.isConsentValid && <ConsentSelection />}
            </Container>
            {!createAvatarStore.isConsentValid && script && (
                <Teleprompter
                    text={scriptWithUsername}
                    open={teleprompterOpen}
                    setOpen={setTeleprompterOpen}
                    onChange={handleVideo}
                    wordsPerMinute={120}
                    isStaticText
                />
            )}
        </>
    );
});
